import { ref, watch } from "vue"
import { debounce, DEBOUNCE_DELAY } from "@/helpers/debounce.js"
import { GRANULARITY } from "@/helpers/energyBillConstants.js"
import {
  cachedDataFetch,
  makeDataFetchCache,
} from "@/services/repowerAPIService.js"
import useNotificationStore from "@/stores/notificationStore.js"

/**
 * Fetches monthly (MS), daily (1D) and — on demand — quarter-hour (15min)
 * allocation records for the new results page. Monthly data drives line totals,
 * daily data drives the chart and per-day detail tables, quarter data is only
 * fetched when the user opts into the "per kwartier" detail view.
 *
 * @param {() => { id: number } | null} meteringPointGetter
 * @param {() => Date[]} monthDateRangeGetter  [startOfMonth, endOfMonth]
 * @param {() => string} [granularityGetter]
 */
export default function useEnergyBillData(
  meteringPointGetter,
  monthDateRangeGetter,
  granularityGetter = () => GRANULARITY.DAY,
) {
  const notificationStore = useNotificationStore()

  const monthlyData = ref(null)
  const dailyData = ref(null)
  const quarterData = ref(null)
  const loading = ref(false)
  const loadingQuarter = ref(false)
  const loadingFailed = ref(false)
  const quarterFailed = ref(false)

  const monthlyCache = makeDataFetchCache()
  const dailyCache = makeDataFetchCache()
  const quarterCache = makeDataFetchCache()

  function pushError(error) {
    notificationStore.pushError(
      "Fout bij het ophalen van resultaten",
      `De resultaten konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error?.code})`,
      "energy-bill-load-error",
    )
  }

  const loadBaseData = debounce((meteringPoint, dateRange) => {
    let pending = 2
    let succeeded = 0
    let hasErrored = false

    function onDone() {
      pending--
      if (pending > 0) {
        return
      }
      loading.value = false
      // Only surface the full-page error state when nothing usable came back;
      // a partial failure still renders the data that did load (the failed
      // request is reported via the notification below).
      if (succeeded === 0) {
        loadingFailed.value = true
      }
    }

    function onSuccess(target) {
      return (data) => {
        target.value = data
        succeeded++
        onDone()
      }
    }

    function onFail(error) {
      if (!hasErrored) {
        hasErrored = true
        pushError(error)
      }
      onDone()
    }

    const endpoint = `/records/${meteringPoint.id}/`

    cachedDataFetch(
      endpoint,
      monthlyCache,
      "MS",
      dateRange,
      onSuccess(monthlyData),
      onFail,
    )

    cachedDataFetch(
      endpoint,
      dailyCache,
      "1D",
      dateRange,
      onSuccess(dailyData),
      onFail,
    )
  }, DEBOUNCE_DELAY)

  const fetchQuarterData = debounce((meteringPoint, dateRange) => {
    cachedDataFetch(
      `/records/${meteringPoint.id}/`,
      quarterCache,
      "15min",
      dateRange,
      (data) => {
        quarterData.value = data
        loadingQuarter.value = false
      },
      // A failed quarter fetch stays local to the detail/chart view; it must
      // not blank the whole page via `loadingFailed`.
      (error) => {
        quarterFailed.value = true
        pushError(error)
        loadingQuarter.value = false
      },
    )
  }, DEBOUNCE_DELAY)

  /**
   * Flags the load before the debounced fetch starts, so consumers can show a
   * loading state during the debounce window instead of an empty one.
   */
  function loadQuarterData(meteringPoint, dateRange) {
    loadingQuarter.value = true
    quarterFailed.value = false
    fetchQuarterData(meteringPoint, dateRange)
  }

  function runLoad(meteringPoint, dateRange) {
    monthlyData.value = null
    dailyData.value = null
    quarterData.value = null
    loadingFailed.value = false
    quarterFailed.value = false
    if (!meteringPoint) {
      loading.value = false
      return
    }
    loading.value = true
    loadBaseData(meteringPoint, dateRange)
    if (granularityGetter() === GRANULARITY.QUARTER) {
      loadQuarterData(meteringPoint, dateRange)
    }
  }

  watch(
    [meteringPointGetter, monthDateRangeGetter],
    ([mp, dateRange]) => runLoad(mp, dateRange),
    { immediate: true },
  )

  watch(granularityGetter, (g) => {
    if (g !== GRANULARITY.QUARTER || quarterData.value !== null) {
      return
    }
    const mp = meteringPointGetter()
    if (mp) {
      loadQuarterData(mp, monthDateRangeGetter())
    }
  })

  function reload() {
    runLoad(meteringPointGetter(), monthDateRangeGetter())
  }

  function reloadQuarter() {
    const mp = meteringPointGetter()
    if (mp) {
      loadQuarterData(mp, monthDateRangeGetter())
    }
  }

  return {
    monthlyData,
    dailyData,
    quarterData,
    loading,
    loadingQuarter,
    loadingFailed,
    quarterFailed,
    reload,
    reloadQuarter,
  }
}
